import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { dispatch } from 'store'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { projectDataThunks } from 'store/features/projectData/thunks'
import { IMediaFile } from 'store/features/projectData/thunks/media'
import { sortArraysByKey } from 'store/features/projectData/utils'
import { handleApiError } from 'store/utils'
import { ErrorKeys } from 'consts/errorKeys'
import { videoTypes } from 'consts'
import { IMediaItem, MediaStatus, TMediaItemType } from 'types/media'
import { extractFrameAtTimestamp } from 'services/video-thumbs/utils'
import { scrollToElementById } from 'utils'
import { calculateTotalMediaDuration } from 'utils/media'
import { useTimelineContext } from 'context/TimelineContext'
import useDurationLimitCheck from 'hooks/useDurationLimitCheck'
import { useGetOverlappingVideoItem } from './useGetOverlappingVideoItem'
import { getPlaceholderMediaItem, getTimestampWithinMediaItem } from './utils'
import { FileRegistry } from 'utils/file-registry/FileRegistry'

const fileRegistry = FileRegistry.getInstance()

export const useFrameExtraction = () => {
  const canvasDefaultDimensions = useSelector(
    projectDataSelectors.defaultDimensions
  )
  const mediaItems = useSelector(projectDataSelectors.mediaItems)
  const { getCurrentTimeImmediate, isPlaying } = useTimelineContext()
  const { isDurationAllowed } = useDurationLimitCheck()
  const { overlappingVideoItem } = useGetOverlappingVideoItem()

  const isButtonDisabled = !overlappingVideoItem || isPlaying

  const extractAndUploadFrame = useCallback(
    async (
      videoPath: string,
      clampedTimestamp: number,
      placeholderItem: IMediaItem
    ) => {
      try {
        const frameBlob = await extractFrameAtTimestamp(
          videoPath,
          clampedTimestamp
        )

        const frameBlobUrl = fileRegistry.registerFile(frameBlob)

        try {
          dispatch(
            projectDataThunks.updateMediaItemWithoutUndoSave({
              id: placeholderItem.id,
              updates: {
                meta: {
                  ...placeholderItem.meta,
                  path: frameBlobUrl
                }
              }
            })
          )

          const frameFileName = `frame-${placeholderItem.id}.jpg`
          const frameFile = new File([frameBlob], frameFileName, {
            type: 'image/jpeg'
          })

          const uploadedMedias = (await dispatch(
            projectDataThunks.uploadMedias([
              {
                id: placeholderItem.id,
                type: 'image',
                file: frameFile
              }
            ])
          )) as IMediaFile[]

          if (!uploadedMedias[0]) {
            throw new Error('Failed to upload frame')
          }

          dispatch(
            projectDataThunks.updateMediaItemWithoutUndoSave({
              id: placeholderItem.id,
              updates: {
                meta: {
                  ...placeholderItem.meta,
                  path: uploadedMedias[0].filePath,
                  originalWidth: uploadedMedias[0].width,
                  originalHeight: uploadedMedias[0].height,
                  status: MediaStatus.OK
                }
              }
            })
          )
        } catch {
          dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
          dispatch(projectDataThunks.removeVisualMediaItem(placeholderItem.id))
        } finally {
          fileRegistry.deleteFile(frameBlobUrl)
        }
      } catch {
        dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
      }
    },
    []
  )

  const adjustTimelineForNewItem = useCallback(
    (
      placeholderItem: IMediaItem,
      overlappingVideoId: string,
      mediaItems: IMediaItem[]
    ) => {
      const sortedMediaItems = sortArraysByKey('startTime', mediaItems)
      const overlappingVideoIndex = sortedMediaItems.findIndex(
        item => item.id === overlappingVideoId
      )

      const extractedFrameIndex = overlappingVideoIndex + 1
      sortedMediaItems.splice(extractedFrameIndex, 0, placeholderItem)
      //to keep track of already changed item without changing the array's values
      let lastEndTime = placeholderItem.startTime + placeholderItem.duration

      const itemsToUpdate: Array<{
        id: string
        left: number
        type: TMediaItemType
      }> = []

      for (let i = extractedFrameIndex + 1; i < sortedMediaItems.length; i++) {
        const nextItem = sortedMediaItems[i]

        if (!nextItem || nextItem.startTime >= lastEndTime) {
          break
        }

        const adjustedStart = lastEndTime
        // update lastEndTime for the next loop based on the current item's duration
        lastEndTime = adjustedStart + nextItem.duration

        itemsToUpdate.push({
          id: nextItem.id,
          left: adjustedStart,
          type: nextItem.type
        })
      }

      const hasItemsToUpdate = itemsToUpdate.length > 0

      if (hasItemsToUpdate) {
        dispatch(projectDataThunks.updateTimelineItems(itemsToUpdate))
      }

      dispatch(
        projectDataThunks.addMediaItems({
          value: [placeholderItem],
          config: { applySelection: false, isUndoable: !hasItemsToUpdate }
        })
      )
    },
    []
  )

  const handleExtractFrame = useCallback(async () => {
    const videoPath = overlappingVideoItem?.meta?.path
    if (
      !overlappingVideoItem ||
      !videoTypes.includes(overlappingVideoItem.type) ||
      !videoPath
    ) {
      return
    }

    const currentTimelineTime = getCurrentTimeImmediate()
    const clampedTimestamp = getTimestampWithinMediaItem(
      overlappingVideoItem,
      currentTimelineTime
    )

    const extractedFrameStartTime =
      overlappingVideoItem.startTime + overlappingVideoItem.duration

    const placeholderItem = getPlaceholderMediaItem(
      extractedFrameStartTime,
      canvasDefaultDimensions,
      overlappingVideoItem
    )

    const totalNewDuration = calculateTotalMediaDuration([placeholderItem])
    if (!isDurationAllowed(totalNewDuration)) {
      return
    }

    adjustTimelineForNewItem(
      placeholderItem,
      overlappingVideoItem.id,
      mediaItems
    )
    scrollToElementById(placeholderItem.id)

    await extractAndUploadFrame(videoPath, clampedTimestamp, placeholderItem)
  }, [
    overlappingVideoItem,
    getCurrentTimeImmediate,
    canvasDefaultDimensions,
    isDurationAllowed,
    adjustTimelineForNewItem,
    extractAndUploadFrame,
    mediaItems
  ])

  return {
    handleExtractFrame,
    isButtonDisabled
  }
}
